<template>
    <section class="dashboard">
        <h3 class="page-title">{{ $t('dashboard.name') }}</h3>

        <!-- Rząd 1 — bieżące okresy. Karty rysują się od razu; do czasu
             odpowiedzi API zamiast liczb stoją szare szkielety. -->
        <div class="row">
            <div class="col-sm-6 col-xl-3 grid-margin stretch-card" v-for="card in model.periodCards" :key="card.key">
                <div class="card card-statistics">
                    <div class="card-body">
                        <h6 class="card-title text-gray mb-3">{{ card.title }}</h6>
                        <template v-if="loading">
                            <span class="skeleton skeleton-number"></span>
                            <span class="skeleton skeleton-text"></span>
                            <span class="skeleton skeleton-caption"></span>
                        </template>
                        <template v-else>
                            <h2 class="font-weight-bold mb-1">{{ card.orders }}</h2>
                            <p class="text-info font-weight-medium mb-2">{{ card.gross }}</p>
                            <p class="dashboard-caption text-gray mb-1">{{ card.paidCaption }}</p>
                            <p v-if="card.delta" class="dashboard-caption mb-0" :class="card.delta.textClass">
                                <i v-if="card.delta.iconClass" :class="card.delta.iconClass"></i>{{ card.delta.text }}
                                <span class="text-gray">{{ card.delta.caption }}</span>
                            </p>
                        </template>
                    </div>
                </div>
            </div>
        </div>

        <!-- Rząd 2 — podsumowanie ostatnich 30 dni. Karta największego
             zamówienia prowadzi do jego szczegółów. -->
        <div class="row">
            <div class="col-sm-6 col-xl-3 grid-margin stretch-card" v-for="card in model.summaryCards" :key="card.key">
                <div class="card card-statistics"
                     :class="{ clickable: card.link }"
                     :role="card.link ? 'button' : null"
                     @click="openCard(card)">
                    <div class="card-body">
                        <h6 class="card-title text-gray mb-3">{{ card.title }}</h6>
                        <template v-if="loading">
                            <span class="skeleton skeleton-number"></span>
                            <span class="skeleton skeleton-caption"></span>
                        </template>
                        <template v-else>
                            <h2 class="font-weight-bold mb-1">{{ card.value }}</h2>
                            <p class="dashboard-caption text-gray mb-0">{{ card.caption }}</p>
                        </template>
                    </div>
                </div>
            </div>
        </div>

        <!-- Rząd 3 — 30 dni dzień po dniu: słupki to liczba zamówień,
             linia (prawa oś) to obrót. -->
        <div class="row">
            <div class="col-lg-12 grid-margin stretch-card">
                <div class="card">
                    <div class="card-body">
                        <h2 class="card-title">{{ model.chart.title }}</h2>
                        <p class="card-description">{{ model.chart.description }}</p>
                        <span v-if="loading" class="skeleton skeleton-chart"></span>
                        <p v-else-if="model.chart.empty" class="text-gray mb-0">{{ $t('dashboard.noData') }}</p>
                        <orders-chart v-else
                                      :chart-data="model.chart.data"
                                      :chart-options="model.chart.options"
                                      :styles="chartStyles"/>
                    </div>
                </div>
            </div>
        </div>

        <!-- Rząd 4 — dotychczasowy ekran główny: lista opłaconych zamówień. -->
        <order-request-payed-list :limit="5"/>
    </section>
</template>

<script>
    import OrdersChart from '../../components/OrdersChart'
    import OrderRequestPayedList from './store/OrderRequestPayedList'
    import dashboard, { buildDashboardModel } from '../../mixins/Dashboard'
    import { Const } from '../../config'

    export default {
        name: 'Dashboard',
        components: { OrdersChart, OrderRequestPayedList },
        // Pobranie statystyk, formatowanie i przeliczenie payloadu na karty
        // i dataset wykresu siedzą w mixinie — patrz src/mixins/Dashboard.js.
        mixins: [dashboard],
        computed: {
            // Jedno miejsce, w którym powstaje cała treść ekranu. Przed
            // odpowiedzią API stats jest nullem, więc model ma same „—" —
            // szablon nigdy nie dostaje NaN ani undefined.
            model() {
                return buildDashboardModel(this.stats, {
                    locale: Const.LANG,
                    translate: (key, params) => this.$t(key, params)
                })
            },
            // chart.js z maintainAspectRatio: false bierze wysokość z rodzica,
            // więc musi ją dostać wprost.
            chartStyles() {
                return { height: '320px', position: 'relative' }
            }
        }
    }
</script>

<style scoped>
    .clickable {
        cursor: pointer;
    }

    /* Kwota nigdy nie łamie się w środku liczby — gdy jest za ciasno,
       kolumny i tak spadają do dwóch na rząd (col-sm-6). */
    .card-statistics h2 {
        white-space: nowrap;
    }

    /* Tytuły kart podsumowania bywają 1-2 liniowe — wspólna wysokość trzyma
       wartości w jednej linii bazowej w całym rzędzie. */
    .card-statistics .card-title {
        min-height: 2.4em;
    }

    .dashboard-caption {
        font-size: 0.75rem;
        line-height: 1.2;
    }

    /* Szkielet ładowania — zwykły gradient w tle, bez dodatkowej zależności. */
    .skeleton {
        display: block;
        background-color: #ebedf2;
        background-image: linear-gradient(90deg, #ebedf2 25%, #f5f6f8 37%, #ebedf2 63%);
        background-size: 400% 100%;
        border-radius: 3px;
        animation: dashboard-skeleton 1.4s ease infinite;
    }

    .skeleton-number {
        width: 60%;
        height: 1.9rem;
        margin-bottom: 0.6rem;
    }

    .skeleton-text {
        width: 80%;
        height: 1.1rem;
        margin-bottom: 0.6rem;
    }

    .skeleton-caption {
        width: 90%;
        height: 0.7rem;
    }

    .skeleton-chart {
        width: 100%;
        height: 320px;
    }

    @keyframes dashboard-skeleton {
        0% {
            background-position: 100% 50%;
        }

        100% {
            background-position: 0 50%;
        }
    }

    /* Migotanie tła jest tu wyłącznie dekoracyjne — przy wyłączonych
       animacjach zostaje sam szary prostokąt. */
    @media (prefers-reduced-motion: reduce) {
        .skeleton {
            animation: none;
        }
    }
</style>
